// ---------------------------------------------------------------------------
// ComparisonValuePickerModal -- Step 2: Choose Specific Value
// ---------------------------------------------------------------------------
// Modal for selecting specific value for a chosen dimension.
// Adapted from Funnel's ValuePickerModal but simplified for Compare.
// Supports search mode (brands, teams, etc.) and list mode (platforms, etc.)
// ---------------------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import type { SearchResultItem, ListResultItem } from '@/types/comparison';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ValueOption {
  readonly id: number | string;
  readonly name: string;
  readonly subtitle?: string;
  /** Optional platform identifier for channel options (e.g., 'Instagram', 'TikTok') */
  readonly platform?: string;
  /** Optional count to display as right-aligned badge (e.g., posts count for channels) */
  readonly count?: number;
}

interface ComparisonValuePickerModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly dimensionLabel: string;
  readonly values: readonly ValueOption[];
  readonly isLoading?: boolean;
  readonly searchMode?: boolean;
  readonly onSearch?: (query: string) => void;
  readonly isSearching?: boolean;
  readonly onApply: (selectedValue: SearchResultItem | ListResultItem) => void;
  /** When true, values are preloaded (e.g. channels) — skip "type 2 chars" message */
  readonly preloaded?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ComparisonValuePickerModal({
  open,
  onClose,
  dimensionLabel,
  values,
  isLoading = false,
  searchMode = false,
  onSearch,
  isSearching = false,
  onApply,
  preloaded = false,
}: ComparisonValuePickerModalProps) {
  const [selected, setSelected] = useState<number | string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setSearchQuery('');
    }
  }, [open]);

  // Debounced search for API search-mode dimensions
  useEffect(() => {
    if (!searchMode || !onSearch || searchQuery.length < 2) return;

    const timer = setTimeout(() => {
      onSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchMode, onSearch]);

  const handleSelect = (value: number | string) => {
    setSelected(value);
  };

  const handleApply = () => {
    if (selected !== null) {
      const selectedOption = values.find((opt) => opt.id === selected);
      if (selectedOption) {
        onApply({
          id: selectedOption.id,
          name: selectedOption.name,
          subtitle: selectedOption.subtitle,
          platform: selectedOption.platform,
        });
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Sort values by count (highest first), then filter locally
  const sortedValues = React.useMemo(() => {
    return [...values].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }, [values]);

  const filteredValues = React.useMemo(() => {
    if (searchQuery.length === 0) return sortedValues;
    if (searchMode && !preloaded) return sortedValues;
    const q = searchQuery.toLowerCase();
    return sortedValues.filter((opt) =>
      opt.name.toLowerCase().includes(q) ||
      (opt.platform && opt.platform.toLowerCase().includes(q))
    );
  }, [sortedValues, searchQuery, searchMode, preloaded]);

  const displayValues = filteredValues;

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-v11-text-primary">
            Select {dimensionLabel}
          </DialogTitle>
          <DialogDescription className="text-sm text-v11-text-muted">
            Choose a specific {dimensionLabel.toLowerCase()} to compare
          </DialogDescription>
        </DialogHeader>

        {/* Search input */}
        {(searchMode || values.length > 10) && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-v11-text-muted" />
            <Input
              placeholder={
                searchMode
                  ? `Search ${dimensionLabel.toLowerCase()}...`
                  : `Search all ${values.length} options...`
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
        )}

        <div className="space-y-2 max-h-[60vh] overflow-y-auto overflow-x-hidden">
          {isLoading || isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-v11-text-muted" />
              <span className="ml-2 text-sm text-v11-text-muted">
                Loading options...
              </span>
            </div>
          ) : searchMode && !preloaded && searchQuery.length < 2 ? (
            <div className="text-center py-8 text-sm text-v11-text-muted">
              Type at least 2 characters to search.
            </div>
          ) : displayValues.length === 0 && searchQuery.length > 0 ? (
            <div className="text-center py-8 text-sm text-v11-text-muted">
              No matches for &ldquo;{searchQuery}&rdquo;
            </div>
          ) : displayValues.length === 0 ? (
            <div className="text-center py-8 text-sm text-v11-text-muted">
              No options available for this dimension.
            </div>
          ) : (
            displayValues.map((option) => {
              const platformConfig = option.platform ? getPlatformIcon(option.platform) : null;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={cn(
                    'w-full p-3 rounded-lg border transition-all',
                    'bg-v11-surface border-v11-border',
                    'hover:border-indigo-500/40 hover:bg-indigo-500/5',
                    'flex items-center gap-3'
                  )}
                >
                  <div
                    className={cn(
                      'w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-all',
                      selected === option.id
                        ? 'bg-indigo-500 border-indigo-500'
                        : 'border-v11-border'
                    )}
                  >
                    {selected === option.id && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>

                  {/* Platform icon (for channels) */}
                  {platformConfig && (
                    <span
                      className="shrink-0 flex items-center justify-center"
                      style={{ color: platformConfig.color }}
                      title={platformConfig.name}
                    >
                      {platformConfig.icon}
                    </span>
                  )}

                  <span className="flex-1 text-left text-sm font-medium text-v11-text-primary truncate min-w-0">
                    {option.name}
                  </span>

                  {option.count !== undefined && (
                    <span className="shrink-0 text-xs font-medium text-indigo-400 tabular-nums">
                      {formatCount(option.count)}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={selected === null}
            className="bg-indigo-500 hover:bg-indigo-600"
          >
            Apply Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
